import { API_BASE_URL } from "src/config/apiBase";
import React, { useState, useMemo, useEffect } from "react";
import { Search } from "lucide-react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import slugify from "./slugify";
import { decryptToken } from "../utils/getToken";
import { getDecryptedUserFromCookie } from "../utils/getCookie";
import OmsSpinnerData from "../Loader/Spinner";




const formatDate = (iso) => {
    try {
        const d = new Date(iso);
        return d.toLocaleString(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: false, // 24-hour format; set true if you want AM/PM
        });
    } catch {
        return iso;
    }
};


const months = [
    "All Months",
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
];

const AllBlogs = () => {
    const [selected, setSelected] = useState([]);
    const [categories, setCategories] = useState([]);
    const [activeTab, setActiveTab] = useState("All");
    const [search, setSearch] = useState("");
    const [selectedMonth, setSelectedMonth] = useState("All Months");
    const [entriesPerPage, setEntriesPerPage] = useState(5); // Default entries per page
    const [currentPage, setCurrentPage] = useState(1); // Track current page
    const url = API_BASE_URL
    const [blogs, setBlogs] = useState([]);
    const [status, setStatus] = useState("");
    const [selectedCategory, setSelectedCategory] = useState("");
    const navigate = useNavigate();
    const token = decryptToken("accessToken") || ""
    const isLoggedIn = getDecryptedUserFromCookie("gta_mainstateuser");
    const [load, setLoad] = useState(false);
    const allIds = useMemo(() => blogs.map((b) => b.id), [blogs]);




    const handleBulkTrash = async () => {
        if (selected.length === 0) return;
        if (!window.confirm("Move selected categories to trash?")) return;

        try {
            const response = await fetch(`${url}/blog/bulk/trash`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ ids: selected, trash: true }),
            });

            if (response.ok) {
                setBlogs((prev) =>
                    prev.map((cat) =>
                        selected.includes(cat.id)
                            ? { ...cat, trash: true }
                            : cat
                    )
                );
                setSelected([]);
            } else {
                const err = await response.json();
                console.error("Bulk trash error:", err.message);
            }
        } catch (error) {
            console.error("Error bulk trashing categories:", error);
        }
    };

    const handleBulkRestore = async () => {
        if (selected.length === 0) return;
        if (!window.confirm("Restore selected categories from trash?")) return;

        try {
            const response = await fetch(`${url}/blog/bulk/trash`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ ids: selected, trash: false }),
            });

            if (response.ok) {
                setBlogs((prev) =>
                    prev.map((cat) =>
                        selected.includes(cat.id)
                            ? { ...cat, trash: false }
                            : cat
                    )
                );
                setSelected([]);
            } else {
                const err = await response.json();
                console.error("Bulk restore error:", err.message);
            }
        } catch (error) {
            console.error("Error bulk restoring categories:", error);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to permanently delete this blog?")) return;

        try {
            const response = await fetch(`${url}/blog/${id}`, {
                method: "DELETE",
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });

            if (response.ok) {
                setBlogs((prev) => prev.filter((cat) => cat.id !== id));
            } else {
                const err = await response.json();
                console.error("Error deleting category:", err.message);
            }
        } catch (error) {
            console.error("Error deleting category:", error);
        }
    };



    const handleTrash = async (id) => {
        try {
            const response = await fetch(`${url}/blog/trash/${id}`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({ trash: true }),
            });

            if (response.ok) {
                setBlogs((prev) =>
                    prev.map((item) =>
                        item.id === id
                            ? { ...item, trash: true }
                            : item
                    )
                );
            } else {
                const err = await response.json();
                console.error("Error:", err.message);
            }
        } catch (error) {
            console.error("Error trashing category:", error);
        }
    }

    // 🧹 Bulk Delete
    const handleBulkDelete = async () => {
        if (selected.length === 0) return;
        if (!window.confirm("Are you sure you want to permanently delete selected categories?")) return;

        try {
            const response = await fetch(`${url}/blog/bulk`, {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({ ids: selected }),
            });

            if (response.ok) {
                setBlogs((prev) =>
                    prev.filter((cat) => !selected.includes(cat.id))
                );
                setSelected([]);
            } else {
                const err = await response.json();
                console.error("Error bulk deleting:", err.message);
            }
        } catch (error) {
            console.error("Error bulk deleting categories:", error);
        }
    };


    const handleRestore = async (id) => {
        try {
            const response = await fetch(`${url}/blog/trash/${id}`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({ trash: false }),
            });

            if (response.ok) {
                setBlogs((prev) =>
                    prev.map((item) =>
                        item.id === id
                            ? { ...item, trash: false }
                            : item
                    )
                );
            }
        } catch (error) {
            console.error("Error restoring category:", error);
        }
    }

    const fetchBlogs = async () => {
        setLoad(true);
        try {
            const response = await fetch(`${url}/blog?admin=true`, {
                method: "GET",
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });
            const res = await response.json();
            if (res.success) {
                // Map backend data to frontend structure
                const mapped = res.data.map((b) => ({
                    id: b.id,
                    trash: b.trash,
                    title: b.title,
                    author_name: b.author_name || "N/A", // replace if author exists
                    date: b.createdAt,
                    status: b.status,
                    categories: b.categories.map(
                        (c) =>
                            c.categoryDetails?.name +
                            (c.categoryDetails?.parentDetails
                                ? ` (${c.categoryDetails.parentDetails.name})`
                                : "")
                    ),
                    feature_image: b.feature_image,
                    slug: b.slug,
                    sections: b.sections,
                    visibility: b.visibility, // ✅ added
                    password: b.password, // ✅ added
                    tags: b.tags.map((t) => t.tagDetails?.name),
                }));
                setLoad(false)
                setBlogs(mapped);
            }
        } catch (error) {
            console.error(error);
            setLoad(false)
        }
    };

    const fetchDataCat = async () => {
        try {
            const response = await fetch(`${url}/blog-category`, {
                method: "GET",
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });
            const res = await response.json();
            setCategories(res.data);
        } catch (error) {
            console.log(error);
        }
    }

    

    // Function to toggle blog status
    // const toggleBlogStatus = async (blogId, currentStatus) => {
    //     const newStatus = currentStatus === "Publish" ? "Draft" : "Publish";
    //     try {
    //         const response = await fetch(`${url}/api/blog/toggle/${blogId}`, {
    //             method: "PATCH",
    //             headers: { "Content-Type": "application/json" },
    //             body: JSON.stringify({ status: newStatus }),
    //         });

    //         const resData = await response.json();

    //         if (response.ok) {
    //             // Update frontend state
    //             toast.success(`${resData.message}`, {
    //                 position: "top-center",
    //                 autoClose: 2000
    //             })
    //             setBlogs(prev =>
    //                 prev.map(b => (b.id === blogId ? { ...b, status: newStatus } : b))
    //             );
    //         } else {
    //             alert(resData.message || "Failed to update status");
    //         }
    //     } catch (err) {
    //         console.error(err);
    //         alert("Something went wrong");
    //     }
    // };


    const toggleBlogStatus = async (blogId, currentStatus, currentTrash) => {
        try {
            const response = await fetch(`${url}/blog/toggle/${blogId}`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
            });

            const resData = await response.json();

            if (response.ok) {
                toast.success(resData.message, {
                    position: "top-center",
                    autoClose: 1500,
                });

                setBlogs((prev) =>
                    prev.map((b) =>
                        b.id === blogId
                            ? { ...b, status: resData.data.status === "publish" ? "Publish" : "Draft", trash: resData.data.trash }
                            : b
                    )
                );
            } else {
                toast.error(resData.message || "Failed to update status");
            }
        } catch (err) {
            console.error(err);
            toast.error("Something went wrong");
        }
    };



    const filteredBlogs = blogs.filter((b) => {
        const matchSearch = b.title.toLowerCase().includes(search.toLowerCase());

        const blogMonth = new Date(b.date).toLocaleString("default", { month: "long" });
        const matchMonth = selectedMonth === "All Months" || blogMonth === selectedMonth;

        const matchTab =
            (activeTab === "All" && !b.trash) ||
            (activeTab === "Published" && b.status === "Publish" && !b.trash)
            ||
            (activeTab === "Trashed" && b.trash);
        console.log("activeTab:", activeTab, "b.trash:", b.trash);

        const matchCategory =
            !selectedCategory ||
            b.categories.some(
                (c) => c === selectedCategory || c.includes(`(${selectedCategory})`)
            );

        return matchSearch && matchMonth && matchTab && matchCategory;
    });



    const allSelected = filteredBlogs.length > 0 && filteredBlogs.every((cat) => selected.includes(cat.id));
    const someSelected = filteredBlogs.some((cat) => selected.includes(cat.id)) && !allSelected;

    const toggleSelectAll = () => {
        if (allSelected) {
            setSelected([]); // Deselect all
        } else {
            setSelected(filteredBlogs.map((cat) => cat.id)); // Select all visible categories
        }
    };

    const toggleSelectOne = (id) => {
        if (selected.includes(id)) {
            setSelected(selected.filter((selectedId) => selectedId !== id));
        } else {
            setSelected([...selected, id]);
        }
    };

    // Pagination logic
    const totalPages = Math.ceil(filteredBlogs.length / entriesPerPage);
    const paginatedBlogs = filteredBlogs.slice(0, currentPage * entriesPerPage);

    const handleLoadMore = () => {
        if (currentPage < totalPages) {
            setCurrentPage((prev) => prev + 1);
        }
    };

    

    useEffect(() => {
        fetchBlogs();
        fetchDataCat();
    }, [])

    return (
        <>
         {load && <OmsSpinnerData/>}
        <div className="p-4">
           
            {/* Tabs + Search Bar */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
                <div className="flex flex-wrap gap-2 items-center text-sm">
                    <div className="flex items-center">
                        <button
                            onClick={() => setActiveTab("All")}
                            className={`cursor-pointer ${activeTab === "All"
                                ? "text-indigo-600 font-semibold"
                                : "text-gray-500 hover:text-gray-800"
                                }`}
                        >
                            All
                        </button>
                    </div>
                    <span className="h-4 w-px bg-gray-300 inline-block"></span>
                    <div className="flex items-center">
                        <button
                            onClick={() => setActiveTab("Published")}
                            className={`cursor-pointer ${activeTab === "Published"
                                ? "text-indigo-600 font-semibold"
                                : "text-gray-500 hover:text-gray-800"
                                }`}
                        >
                            Published
                        </button>
                    </div>
                    <span className="h-4 w-px bg-gray-300 inline-block"></span>
                    <div className="flex items-center">
                        <button
                            onClick={() => setActiveTab("Trashed")}
                            className={`cursor-pointer ${activeTab === "Trashed"
                                ? "text-red-600 font-semibold"
                                : "text-gray-500 hover:text-gray-800"
                                }`}
                        >
                            Trashed
                        </button>
                    </div>
                </div>

                <div className="flex gap-2 flex-wrap">
                    <div className="relative w-full md:w-64">
                        <Search
                            size={18}
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                        />
                        <input
                            type="text"
                            placeholder="Search blog..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-indigo-500 focus:outline-none"
                        />
                    </div>
                    <div>
                        <button onClick={() => navigate("/add-blog")} className="oms-add-btn" >Add Blog</button>
                    </div>
                </div>
            </div>

            {/* Filters below header */}
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between mb-4">
                <div className="flex flex-wrap gap-3 items-center text-sm w-full md:w-auto">
                    {/* Entries Per Page */}
                    <select
                        value={entriesPerPage}
                        onChange={(e) => {
                            setEntriesPerPage(Number(e.target.value));
                            setCurrentPage(1); // Reset to first page when changing entries per page
                        }}
                        className="cursor-pointer border border-gray-300 rounded-md px-2 py-1 text-xs md:text-sm w-full md:w-auto"

                    >
                        <option value={5}>5 per page</option>
                        <option value={10}>10 per page</option>
                        <option value={20}>20 per page</option>
                    </select>

                    {/* Filter by Month */}
                    <select
                        value={selectedMonth}
                        onChange={(e) => {
                            setSelectedMonth(e.target.value);
                            setCurrentPage(1); // Reset to first page when changing filter
                        }}
                        className="cursor-pointer border border-gray-300 rounded-md px-2 py-1 text-sm focus:ring-1 focus:ring-indigo-500 outline-none"
                    >
                        {months.map((month) => (
                            <option key={month} value={month}>
                                {month}
                            </option>
                        ))}
                    </select>

                    {/* Filter by Category */}
                    <select
                        value={selectedCategory}
                        onChange={(e) => {
                            setSelectedCategory(e.target.value);
                            setCurrentPage(1);
                        }}
                        className="cursor-pointer border border-gray-300 rounded-md px-2 py-1 text-sm focus:ring-1 focus:ring-indigo-500 outline-none"
                    >
                        <option value="">All Categories</option>
                        {categories.map((cat) => (
                            <option key={cat.id} value={cat.name}>
                                {cat.name}
                            </option>
                        ))}
                    </select>



                    {/* Bulk Trash Button */}
                    {
                        activeTab === "Trashed" ?
                            <button
                                disabled={selected.length === 0}
                                onClick={handleBulkDelete}
                                className={`cursor-pointer px-3 py-1 rounded-md text-sm font-medium ${selected.length === 0
                                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                    : "bg-red-600 text-white hover:bg-red-700"
                                    }`}
                            >
                                Bulk Delete
                            </button> : <button
                                disabled={selected.length === 0}
                                onClick={handleBulkTrash}
                                className={`cursor-pointer px-3 py-1 rounded-md text-sm font-medium ${selected.length === 0
                                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                    : "bg-red-600 text-white hover:bg-red-700"
                                    }`}
                            >
                                Bulk Trash
                            </button>
                    }
                    {
                        activeTab === "Trashed" &&
                        <button
                            disabled={selected.length === 0}
                            onClick={handleBulkRestore}
                            className={`cursor-pointer px-3 py-1 rounded-md text-sm font-medium ${selected.length === 0
                                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                : "bg-blue-600 text-white hover:bg-blue-700"
                                }`}
                        >
                            Bulk Restore
                        </button>
                    }
                </div>

                <div className="text-sm text-gray-600 w-full md:w-auto text-left md:text-right">
                    Showing {paginatedBlogs.length} of {filteredBlogs.length} posts
                </div>
            </div>

            {/* Table */}
            <div className="bg-white shadow rounded-lg overflow-hidden w-full">
                <div className="overflow-x-auto w-full">
                    <table className="min-w-[800px] w-full table-auto">
                        <thead className="bg-[#f9f9f9] text-gray-900">
                            <tr>
                                <th className="px-4 py-3 text-left w-12">
                                    <label className="inline-flex items-center">
                                        <input
                                            type="checkbox"
                                            className="form-checkbox h-4 w-4 text-blue-500 rounded"
                                            checked={allSelected}
                                            ref={(el) => {
                                                if (el) el.indeterminate = someSelected;
                                            }}
                                            onChange={toggleSelectAll}
                                            aria-label="Select all blogs"
                                        />
                                    </label>
                                </th>
                                <th className="px-4 py-3 text-left text-sm font-medium tracking-wider">
                                    Title
                                </th>
                                <th className="px-4 py-3 text-left text-sm font-medium tracking-wider">
                                    Author
                                </th>
                                <th className="px-4 py-3 text-left text-sm font-medium tracking-wider">
                                    Categories
                                </th>
                                <th className="px-4 py-3 text-left text-sm font-medium tracking-wider">
                                    Status
                                </th>
                                <th className="px-4 py-3 text-left text-sm font-medium tracking-wider">
                                    Visibility
                                </th>
                                <th className="px-4 py-3 text-left text-sm font-medium tracking-wider">
                                    Date
                                </th>
                            </tr>
                        </thead>

                        <tbody className="divide-y">
                            {paginatedBlogs.map((blog) => {
                                const checked = selected.includes(blog.id);
                                return (
                                    <tr
                                        key={blog.id}
                                        className="group hover:bg-gray-50 transition border-b border-gray-200"
                                    >
                                        <td className="px-4 py-2">
                                            <label className="inline-flex items-center">
                                                <input
                                                    type="checkbox"
                                                    className="form-checkbox h-4 w-4 text-blue-600 rounded"
                                                    checked={checked}
                                                    onChange={() => toggleSelectOne(blog.id)}
                                                    aria-label={`Select blog ${blog.title}`}
                                                />
                                            </label>
                                        </td>
                                        <td className="px-4 py-2 max-w-xs">
                                            <div className="flex flex-col">
                                                <a
                                                    href="#"
                                                    className="text-gray-900 font-medium truncate transition"
                                                >
                                                    {blog.title}
                                                </a>
                                                <div className="opacity-0 group-hover:opacity-100 transition-opacity mt-1 flex items-center gap-2 text-xs">
                                                    <button
                                                        onClick={() => navigate(`/edit-blog/${blog.id}`)}
                                                        className="cursor-pointer text-gray-500 hover:text-indigo-600 font-medium">
                                                        Edit
                                                    </button>
                                                    <span className="h-3 w-px bg-gray-300"></span>
                                                    {
                                                        blog.trash === true && <button
                                                            onClick={() => handleRestore(blog.id)}
                                                            className="cursor-pointer text-gray-500 hover:text-green-600 font-medium">
                                                            Restore
                                                        </button>

                                                    }
                                                    <span className="h-3 w-px bg-gray-300"></span>
                                                    {
                                                        blog.trash === true ?
                                                            <button
                                                                onClick={() => handleDelete(blog.id)}
                                                                className="cursor-pointer text-red-600 font-medium">
                                                                Delete
                                                            </button> :
                                                            <button
                                                                onClick={() => handleTrash(blog.id)}
                                                                className="cursor-pointer text-red-600 font-medium">
                                                                Trash
                                                            </button>
                                                    }
                                                    <span className="h-3 w-px bg-gray-300"></span>
                                                    <button onClick={() => navigate(`/admin/preview-blog/${slugify(blog.slug)}/${blog.id}`)} className="cursor-pointer text-gray-500 hover:text-green-600 font-medium">
                                                        View
                                                    </button>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-2 text-sm text-gray-700">
                                            {blog.author_name || "N/A"}
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex flex-wrap gap-2">
                                                {blog.categories.slice(0, 3).map((c, i) => (
                                                    <span
                                                        key={i}
                                                        className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700 border border-gray-200"
                                                    >
                                                        {c}
                                                    </span>
                                                ))}
                                            </div>
                                            {blog.categories.length > 3 && (
                                                <span className="text-xs text-gray-500">
                                                    +{blog.categories.length - 3} more
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-2">
                                            <label
                                                className="relative inline-flex items-center cursor-pointer"
                                                title={
                                                    blog.status === "Publish"
                                                        ? "Click to move to Draft"
                                                        : "Click to Publish"
                                                }
                                            >
                                                <input
                                                    type="checkbox"
                                                    className="sr-only peer"
                                                    checked={blog.status === "Publish"}
                                                    onChange={() => toggleBlogStatus(blog.id, blog.status)}
                                                />

                                                <div
                                                    className={`w-10 h-5 rounded-full transition-colors duration-200 relative ${blog.status === "Publish" ? "bg-green-500" : "bg-gray-400"
                                                        }`}
                                                >
                                                    <div
                                                        className={`w-4 h-4 bg-white rounded-full shadow-md transform transition-transform duration-200 absolute top-0.5 left-0.5 ${blog.status === "Publish" ? "translate-x-5" : "translate-x-0"
                                                            }`}
                                                    ></div>
                                                </div>
                                            </label>
                                        </td>

                                        <td className="px-4 py-2 text-sm">
                                            {blog.visibility === "Public" && (
                                                <span className="text-green-600 font-medium">Public</span>
                                            )}
                                            {blog.visibility === "Private" && (
                                                <span className="text-gray-700 font-medium">Private</span>
                                            )}
                                            {blog.visibility === "Password" && (
                                                <span
                                                    className="text-blue-600 font-medium cursor-pointer"
                                                    title={
                                                        blog.password
                                                            ? "This post is password protected"
                                                            : "Password not set"
                                                    }
                                                >
                                                    Password
                                                </span>
                                            )}
                                        </td>




                                        <td className="px-4 py-2 text-sm text-gray-600">
                                            {formatDate(blog.date)}
                                        </td>
                                    </tr>
                                );
                            })}
                            {paginatedBlogs.length === 0 && (
                                <tr>
                                    <td colSpan={5} className="px-4 py-8 text-center text-gray-600">
                                        No blog posts found.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Load More Text */}
            {paginatedBlogs.length < filteredBlogs.length && (
                <div className="flex justify-center mt-4">
                    <h2
                        onClick={handleLoadMore}
                        className="text-black font-bold text-sm cursor-pointer hover:underline transition"
                    >
                        Load More
                    </h2>
                </div>
            )}

        </div>
        </>
    );
};

export default AllBlogs;